"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { lazyChunk } from "@/lib/lazy-chunk";
import { cn } from "@/lib/utils";
import type { LiveWaveformState } from "./live-waveform";
import { NativeVoice } from "./native-voice";
import { placeholderBars } from "./placeholder-bars";
import { useVoiceEngine } from "./use-voice-engine";
import { VoiceNoteRow } from "./voice-note-row";

// `wavesurfer.js` (~60 KB) is only needed once a voice note is actually used -
// split it out of the initial payload and fetch on demand. A failed fetch is retried
// (capped, with backoff) and then shows an inline "Couldn't load - Retry" under the row.
const loadLiveWaveform = () => import("./live-waveform");
const LiveWaveform = lazyChunk("voice-waveform", loadLiveWaveform, { pick: (m) => m.LiveWaveform });

const BAR_COUNT = 40;
const IDLE: LiveWaveformState = { ready: false, time: 0, failed: false };

/** Static stand-in for the waveform, drawn over wavesurfer's slot until it is ready. */
function PlaceholderWave({ src, onDark }: { src: string; onDark: boolean }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-between"
    >
      {placeholderBars(src, BAR_COUNT).map((h, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-order decorative bars
          key={i}
          style={{ height: `${Math.round(h * 100)}%` }}
          className={cn("w-0.5 rounded-sm", onDark ? "bg-wx-primary-fg/35" : "bg-wx-fg-muted")}
        />
      ))}
    </div>
  );
}

/**
 * A voice note in a message bubble.
 *
 * Starting wavesurfer (fetch + AudioContext + decode + canvas) blocks the main
 * thread for 200-470 ms, and it used to start the moment the bubble mounted - for
 * every voice note in the loaded page, on screen or not, right as a conversation
 * opened. Now the bubble is a light static row (play button, stand-in waveform)
 * until the visitor PRESSES it. Hover and keyboard focus only warm the (code) chunk.
 *
 * The press is the gesture iOS Safari requires, and it is consumed by the tap itself:
 * `useVoiceEngine` creates an HTMLMediaElement and calls `play()` synchronously in the
 * click handler. The waveform (fetch, decode, canvas) is built afterwards, in parallel,
 * and attached to that already-playing element (wavesurfer's `media` option); it only
 * draws, it never starts playback. So a slow or failed waveform (chunk, decode, CORS)
 * never delays or stops the audio. If the waveform cannot be built the note becomes the
 * browser's own player (`NativeVoice`). Only one note plays at a time (`claimPlayback`).
 *
 * The row (and its play button) is ONE element throughout; wavesurfer is a
 * headless sibling that draws into the row's waveform slot. Replacing the button
 * when loading starts would drop a tap whose pointer-down landed on the old one.
 */
export function VoiceNote({ src, onDark = false }: { src: string; onDark?: boolean }) {
  const [live, setLive] = useState<LiveWaveformState>(IDLE);
  const containerRef = useRef<HTMLDivElement>(null);
  const engine = useVoiceEngine(src);
  const failed = live.failed || engine.failed;
  const { stop } = engine;

  // The waveform could not be built: the plain player takes over, so silence this engine.
  useEffect(() => {
    if (failed) stop();
  }, [failed, stop]);

  // Carry on from the tap if the note was playing when it had to fall back.
  if (failed) {
    return <NativeVoice src={src} onDark={onDark} resume={engine.playing || engine.starting} />;
  }

  // Hover / keyboard focus are not gestures: warm the code chunk only (no audio work).
  const warm = () => void loadLiveWaveform().catch(() => {});

  return (
    <>
      <VoiceNoteRow
        onDark={onDark}
        loading={engine.starting}
        isPlaying={engine.playing}
        onPlayPause={engine.toggle}
        onPointerEnter={warm}
        onFocus={warm}
        wave={
          <div ref={containerRef} className="relative h-6 min-w-0 flex-1">
            {!live.ready && <PlaceholderWave src={src} onDark={onDark} />}
          </div>
        }
        time={live.ready ? formatTime(live.time) : "0:00"}
      />
      {engine.audio && (
        <Suspense fallback={null}>
          <LiveWaveform
            src={src}
            onDark={onDark}
            containerRef={containerRef}
            media={engine.audio}
            onState={setLive}
          />
        </Suspense>
      )}
    </>
  );
}

function formatTime(time: number): string {
  const m = Math.floor(time / 60);
  const s = Math.floor(time % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}
